import { useState, useRef } from 'react';
import SeoHead from '@/components/feature/SeoHead';
import { aboutTimelineData, honorsData, contactInfoData } from '@/mocks/data';

function BrandIntro() {
  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
            关于 jiuyou
          </h1>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
            jiuyou成立于2018年，是一家专注于智能服务平台研发与运营的科技公司。
            我们致力于通过技术创新，为亿万用户提供更高效、更安全、更便捷的数字化服务体验。
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 items-center">
          <div>
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20office%20team%20collaboration%20scene%20with%20warm%20lighting%2C%20diverse%20Asian%20professionals%20working%20together%2C%20clean%20minimalist%20interior%2C%20cream%20and%20coral%20accent%20colors%2C%20friendly%20atmosphere%2C%20natural%20light%20from%20large%20windows%2C%20editorial%20photography%20style%2C%20professional%20yet%20approachable&width=700&height=500&seq=about-brand-01&orientation=landscape"
              alt="jiuyou团队"
              className="w-full h-auto rounded-2xl object-cover"
            />
          </div>
          <div>
            <h2 className="text-2xl font-heading font-semibold text-foreground-950 mb-5">我们的使命</h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5">
              jiuyou的使命是<strong>"让每个人都能轻松享受数字科技带来的便利"</strong>。我们相信，好的技术应该是简单、直观、人人可用的。从创立之初，我们就坚持以用户为中心的设计理念，不断打磨产品细节，追求极致的用户体验。
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              经过八年的发展，jiuyou已经成长为拥有500万+用户、200+合作伙伴的行业领先平台。但我们从未忘记初心——用技术的力量，为每一位用户创造真实可见的价值。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Timeline() {
  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100/80 text-primary-700 text-xs font-medium mb-4">
            发展历程
          </span>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">一路走来</h2>
        </div>
        <div className="relative">
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-primary-200/60 md:-translate-x-px" />
          <div className="space-y-10">
            {aboutTimelineData.map((item, idx) => (
              <div
                key={item.year}
                className={`relative flex flex-col md:flex-row gap-6 ${
                  idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                <div className="md:w-1/2 flex items-start gap-4 md:justify-end md:pr-10">
                  <div className={`${idx % 2 === 1 ? 'md:pl-10 md:pr-0' : ''}`} />
                </div>
                <div className="absolute left-4 md:left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-primary-500 border-4 border-background-100 mt-2 z-10" />
                <div className={`md:w-1/2 pl-10 md:pl-0 ${idx % 2 === 0 ? 'md:pl-10' : 'md:pr-10'}`}>
                  <div className="p-5 rounded-xl bg-background-50 border border-background-200/70">
                    <span className="inline-block px-3 py-1 rounded-full bg-primary-100/80 text-primary-700 text-xs font-semibold mb-2">
                      {item.year}
                    </span>
                    <h3 className="text-base font-heading font-semibold text-foreground-900 mb-1">{item.title}</h3>
                    <p className="text-sm text-foreground-600">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Honors() {
  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100/80 text-accent-700 text-xs font-medium mb-4">
            荣誉资质
          </span>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">行业认可</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {honorsData.map((honor, idx) => (
            <div
              key={idx}
              className="p-5 rounded-xl bg-background-50 border border-background-200/70 flex items-start gap-4"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                <i className="ri-award-line text-xl" />
              </div>
              <div>
                <div className="text-sm font-semibold text-foreground-900 mb-1">{honor.title}</div>
                <div className="text-xs text-foreground-500">{honor.issuer} · {honor.year}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  const formUrl = 'https://readdy.ai/api/form/d9rea9cnlsngrm4lmb60';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    if (!formRef.current) return;

    const formData = new FormData(formRef.current);
    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setSubmitted(true);
      return;
    }

    formData.delete('phone_alt');

    if (!formUrl) {
      setError('表单暂时不可用，请通过邮箱联系我们');
      return;
    }

    try {
      const res = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setSubmitted(true);
        formRef.current.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (serverMsg.includes('spam')) {
          setError('提交失败，请稍后重试');
        } else {
          setError(serverMsg || '提交失败，请稍后重试');
        }
      }
    } catch {
      setError('网络错误，请检查网络后重试');
    }
  };

  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100" id="contact">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100/80 text-secondary-700 text-xs font-medium mb-4">
            联系我们
          </span>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">期待与您沟通</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                <i className="ri-map-pin-line text-xl" />
              </div>
              <div>
                <div className="text-sm font-semibold text-foreground-900 mb-1">公司地址</div>
                <div className="text-sm text-foreground-600">{contactInfoData.address}</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                <i className="ri-phone-line text-xl" />
              </div>
              <div>
                <div className="text-sm font-semibold text-foreground-900 mb-1">客服热线</div>
                <div className="text-sm text-foreground-600">{contactInfoData.phone}</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                <i className="ri-mail-line text-xl" />
              </div>
              <div>
                <div className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</div>
                <div className="text-sm text-foreground-600">{contactInfoData.email}</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                <i className="ri-time-line text-xl" />
              </div>
              <div>
                <div className="text-sm font-semibold text-foreground-900 mb-1">工作时间</div>
                <div className="text-sm text-foreground-600">{contactInfoData.workingHours}</div>
              </div>
            </div>
          </div>

          <div>
            {submitted ? (
              <div className="p-8 rounded-2xl bg-background-50 border border-background-200/70 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-check-line text-2xl" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2">提交成功！</h3>
                <p className="text-sm text-foreground-600">感谢您的留言，我们会在24小时内与您联系。</p>
              </div>
            ) : (
              <form
                ref={formRef}
                onSubmit={handleSubmit}
                className="p-6 rounded-2xl bg-background-50 border border-background-200/70"
                data-readdy-form
                id="contact-form"
              >
                <div className="space-y-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/30 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/30 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/30 transition-all resize-none"
                      placeholder="请输入您想了解的内容（500字以内）"
                    />
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="contact-form-anti-spam"
                  />
                  {error && (
                    <div className="p-3 rounded-lg bg-primary-50 text-primary-700 text-sm">{error}</div>
                  )}
                  <button
                    type="submit"
                    className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all duration-200 cursor-pointer"
                  >
                    发送留言
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function About() {
  useEffect(() => {
    const script = document.createElement('script');
    script.id = 'schema-breadcrumb-about';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://jiuyou.com' },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://jiuyou.com/about' },
      ],
    });
    document.head.appendChild(script);
    return () => {
      const s = document.getElementById('schema-breadcrumb-about');
      if (s) s.remove();
    };
  }, []);

  return (
    <>
      <SeoHead
        data={{
          title: '关于我们 - 了解jiuyou品牌故事与团队 | jiuyou官网',
          description: '了解jiuyou的品牌故事、发展历程、荣誉资质与联系方式。jiuyou成立于2018年，服务超过500万用户，覆盖50余个城市，是您值得信赖的智能服务平台。',
          canonicalUrl: 'https://jiuyou.com/about',
        }}
      />
      <BrandIntro />
      <Timeline />
      <Honors />
      <Contact />
    </>
  );
}